import { Box, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { useGetAlleysForCollectionQuery } from '../../../services/entities/impl/blfApi/endpoints/alleys';
import { useGetCollectionQuery } from '../../../services/entities/impl/blfApi/endpoints/collections';
import { useGetScansQuery } from '../../../services/entities/impl/blfApi/endpoints/scans';
import { useGetUploadSummariesQuery } from '../../../services/entities/impl/blfApi/endpoints/uploadSummaries';
import { useGetUploadsForCollectionQuery } from '../../../services/entities/impl/blfApi/endpoints/uploads';
import { CollectionDetailsNavButton } from '../../collections/components/CollectionDetailsNavButton';
import { SCANS_PAGE_REFRESH_INTERVAL_MS } from '../../scans/scansPageConstants';
import DebugInfoSection from '../components/DebugInfoSection';
import {
  selectDebugCurrCollectionId,
  selectDebugRefreshTrigger,
} from '../debugSlice';
import { CollectionErrorAlert } from './CollectionErrorAlert';
import { SearchCollectionHeader } from './SearchCollectionHeader';
import { CollectionNoDataState } from './collectionSummary/CollectionNoDataState';
import { CollectionSummary } from './collectionSummary/CollectionSummary';
import { useJsonLabelsAlleys } from './hooks/labels/useJsonLabelsAlleys';
import { useJsonLabelsScans } from './hooks/labels/useJsonLabelsScans';
import { useJsonLabelsUploads } from './hooks/labels/useJsonLabelsUploads';
import { useCreateCollectionLabel } from './hooks/useCreateCollectionLabel';
import { useAlleysValidation } from './hooks/validation/useAlleysValidation';
import { useCollectionValidation } from './hooks/validation/useCollectionValidation';
import { useScansValidation } from './hooks/validation/useScansValidation';
import { useUploadDetailValidation } from './hooks/validation/useUploadDetailValidation';
import { useUploadSummaryValidation } from './hooks/validation/useUploadSummaryValidation';
import { useUploadsValidation } from './hooks/validation/useUploadsValidation';
import { sortAlleys, sortUploads } from './utils/sortByAlleyNumber';

export const DebugInfoCollections = () => {
  const collectionId = useAppSelector(selectDebugCurrCollectionId);
  const refreshTrigger = useAppSelector(selectDebugRefreshTrigger);

  // Fetch collection data
  const {
    currentData: collection,
    isLoading: isLoadingCollection,
    isFetching: isFetchingCollection,
    isError: isCollectionError,
    error: collectionError,
    refetch: refetchCollection,
  } = useGetCollectionQuery(collectionId, {
    skip: !collectionId,
    pollingInterval: SCANS_PAGE_REFRESH_INTERVAL_MS,
  });

  const collectionLabel = useCreateCollectionLabel(collection);

  // Fetch upload summaries
  const {
    currentData: uploadSummaries,
    isLoading: isLoadingUploadSummaries,
    isFetching: isFetchingUploadSummaries,
    isError: isUploadSummariesError,
    error: uploadSummariesError,
    refetch: refetchUploadSummaries,
  } = useGetUploadSummariesQuery(undefined, {
    skip: !collectionId,
    pollingInterval: SCANS_PAGE_REFRESH_INTERVAL_MS,
  });

  // Find the upload summary for the current collection
  const uploadSummary = useMemo(() => {
    return uploadSummaries?.find((summary) =>
      summary.uploads.some((upload) => upload.collectionID === collectionId)
    );
  }, [uploadSummaries, collectionId]);

  // Find the specific upload detail for the current collection
  const uploadDetail = useMemo(() => {
    if (!uploadSummary) return undefined;
    return uploadSummary.uploads.find(
      (upload) => upload.collectionID === collectionId
    );
  }, [uploadSummary, collectionId]);

  // Fetch scans
  const {
    currentData: scansResponse,
    isLoading: isLoadingScans,
    isFetching: isFetchingScans,
    isError: isScansError,
    error: scansError,
    refetch: refetchScans,
  } = useGetScansQuery(
    {
      collection__id__in: collectionId ? [collectionId] : undefined,
      page: 1,
      size: 1000,
    },
    {
      skip: !collectionId,
      pollingInterval: SCANS_PAGE_REFRESH_INTERVAL_MS,
    }
  );

  const scans = useMemo(() => {
    return scansResponse?.items || [];
  }, [scansResponse]);

  const scanLabels = useJsonLabelsScans(scans);

  // Fetch alleys
  const {
    currentData: alleysCurrentData,
    isLoading: isLoadingAlleys,
    isFetching: isFetchingAlleys,
    isError: isAlleysError,
    error: alleysError,
    refetch: refetchAlleys,
  } = useGetAlleysForCollectionQuery(collectionId, {
    skip: !collectionId,
    pollingInterval: SCANS_PAGE_REFRESH_INTERVAL_MS,
  });

  // Sort the alleys by alley number (ascending)
  const sortedAlleys = useMemo(() => {
    return sortAlleys(alleysCurrentData?.items, {
      ascending: true,
    });
  }, [alleysCurrentData?.items]);

  const alleyLabels = useJsonLabelsAlleys(sortedAlleys);

  // Fetch uploads
  const {
    currentData: uploadsCurrentData,
    isLoading: isLoadingUploads,
    isFetching: isFetchingUploads,
    isError: isUploadsError,
    error: uploadsError,
    refetch: refetchUploads,
  } = useGetUploadsForCollectionQuery(collectionId, {
    skip: !collectionId,
    pollingInterval: SCANS_PAGE_REFRESH_INTERVAL_MS,
  });

  // Sort the uploads by upload date (ascending)
  const sortedUploads = useMemo(() => {
    return sortUploads(uploadsCurrentData?.items, {
      alleyProperty: 'fileName',
      prioritizePatterns: ['collection.json'],
      ascending: true,
    });
  }, [uploadsCurrentData?.items]);

  const uploadLabels = useJsonLabelsUploads(sortedUploads);

  // Trigger refetches when refreshTrigger button is clicked
  useEffect(() => {
    if (refreshTrigger && collectionId) {
      // Refetch all data
      refetchCollection();
      refetchUploadSummaries();
      refetchScans();
      refetchAlleys();
      refetchUploads();
    }
  }, [
    refreshTrigger,
    collectionId,
    refetchCollection,
    refetchUploadSummaries,
    refetchScans,
    refetchAlleys,
    refetchUploads,
  ]);

  // Check if any API call has an error
  const hasErrors =
    isCollectionError ||
    isUploadSummariesError ||
    isScansError ||
    isAlleysError ||
    isUploadsError;

  // Check if all data is completely loaded
  const isAllDataLoaded =
    !isLoadingCollection &&
    !isLoadingUploadSummaries &&
    !isLoadingScans &&
    !isLoadingAlleys &&
    !isLoadingUploads;

  // Check if any data is being fetched (initial or subsequent)
  const isFetchingAnyData =
    isFetchingCollection ||
    isFetchingUploadSummaries ||
    isFetchingScans ||
    isFetchingAlleys ||
    isFetchingUploads;

  // Validation hooks
  const {
    validationResult: collectionValidation,
    summary: collectionValidationSummary,
  } = useCollectionValidation(collection);

  const {
    validationResult: uploadSummaryValidation,
    summary: uploadSummaryValidationSummary,
  } = useUploadSummaryValidation(uploadSummary);

  // Add the new upload detail validation
  const {
    validationResult: uploadDetailValidation,
    summary: uploadDetailValidationSummary,
  } = useUploadDetailValidation(uploadDetail, sortedAlleys, sortedUploads);

  const { individualResults: scanValidations, summary: scanValidationSummary } =
    useScansValidation(scans);

  const {
    individualResults: alleyValidations,
    summary: alleyValidationSummary,
  } = useAlleysValidation(sortedAlleys, sortedUploads);

  const {
    individualResults: uploadValidations,
    summary: uploadValidationSummary,
  } = useUploadsValidation(sortedUploads, sortedAlleys);

  // Determine if we should show loading skeletons
  const shouldShowLoadingSkeletons =
    collectionId &&
    ((isLoadingCollection && !collection) || isFetchingCollection);

  const content = (
    <Stack
      sx={{
        width: '40rem',
        height: 'calc(100% - 3.5rem)', // Subtracting header height
        maxHeight: 'calc(100% - 3.5rem)', // Subtracting header height
        boxSizing: 'border-box', // Include padding and border in box height
        overflowY: 'auto',
        px: 2,
      }}
    >
      <SearchCollectionHeader />

      {/* No collection ID - show prompt */}
      {!collectionId && (
        <CollectionNoDataState
          collectionId={collectionId}
          isCollectionError={false}
          isAllDataLoaded={true}
          hasCollection={false}
        />
      )}

      {/* Loading skeletons while fetching collection */}
      {shouldShowLoadingSkeletons && (
        <CollectionSummary collectionId={collectionId} isLoading={true} />
      )}

      {/* Error state - only show after loading completes and not currently fetching */}
      {collectionId &&
        isCollectionError &&
        !isLoadingCollection &&
        !isFetchingCollection && (
          <CollectionNoDataState
            collectionId={collectionId}
            isCollectionError={true}
            isAllDataLoaded={true}
            hasCollection={false}
          />
        )}

      {/* Not found state - only show after all loading completes and not fetching */}
      {collectionId &&
        !collection &&
        isAllDataLoaded &&
        !isCollectionError &&
        !isFetchingCollection && (
          <CollectionNoDataState
            collectionId={collectionId}
            isCollectionError={false}
            isAllDataLoaded={true}
            hasCollection={false}
          />
        )}

      {/* Show collection data if we have a valid collection and not re-fetching */}
      {collection && !shouldShowLoadingSkeletons && (
        <>
          {/* Show API errors if any */}
          {hasErrors && (
            <Box sx={{ mb: 2 }}>
              <CollectionErrorAlert
                isError={isCollectionError}
                errorData={collectionError}
                entityName='Collection'
              />
              <CollectionErrorAlert
                isError={isUploadSummariesError}
                errorData={uploadSummariesError}
                entityName='Upload Summaries'
              />
              <CollectionErrorAlert
                isError={isScansError}
                errorData={scansError}
                entityName='Scans'
              />
              <CollectionErrorAlert
                isError={isAlleysError}
                errorData={alleysError}
                entityName='Alleys'
              />
              <CollectionErrorAlert
                isError={isUploadsError}
                errorData={uploadsError}
                entityName='Uploads'
              />
            </Box>
          )}

          <CollectionSummary
            collectionId={collectionId}
            uploads={sortedUploads}
            alleys={sortedAlleys}
            scans={scans}
            uploadDetail={uploadDetail}
            collectionValidationCounts={collectionValidationSummary?.counts}
            uploadSummaryValidationCounts={
              uploadSummaryValidationSummary?.counts
            }
            uploadDetailValidationCounts={uploadDetailValidationSummary?.counts}
            scansValidationCounts={scanValidationSummary?.counts}
            alleysValidationCounts={alleyValidationSummary?.counts}
            uploadsValidationCounts={uploadValidationSummary?.counts}
            isLoading={isFetchingAnyData}
          />

          <DebugInfoSection
            sectionLabel={'Collection'}
            name={collectionLabel}
            id={collection?.id}
            isLoading={isLoadingCollection || isFetchingCollection}
            jsonObjectOrArrayOfObjects={collection}
            validationSummary={collectionValidationSummary?.result}
            validationCounts={collectionValidationSummary?.counts}
            itemValidations={collectionValidation}
            actionButtonOrElement={
              <CollectionDetailsNavButton collectionId={collection.id} />
            }
          />

          <DebugInfoSection
            sectionLabel={'Upload Summary'}
            isLoading={isLoadingUploadSummaries || isFetchingUploadSummaries}
            isError={isUploadSummariesError}
            jsonObjectOrArrayOfObjects={uploadSummary}
            jsonLabels={
              uploadSummary
                ? `${uploadSummary?.customerName} [${uploadSummary?.overallPercentUploaded}%, ${uploadSummary?.overallStatus}]`
                : undefined
            }
            showID={false}
            validationSummary={uploadSummaryValidationSummary?.result}
            validationCounts={uploadSummaryValidationSummary?.counts}
            itemValidations={uploadSummaryValidation}
          />

          {/* Add Upload Detail section */}
          <DebugInfoSection
            sectionLabel={'Upload Detail'}
            isLoading={isLoadingUploadSummaries || isFetchingUploadSummaries}
            isError={isUploadSummariesError}
            jsonObjectOrArrayOfObjects={uploadDetail}
            jsonLabels={
              uploadDetail
                ? `${uploadDetail.collectionDate} / ${uploadDetail.cameraID} / ${uploadDetail.ssdID} [${uploadDetail.percentUploaded}%, ${uploadDetail.status}]`
                : undefined
            }
            showID={false}
            validationSummary={uploadDetailValidationSummary?.result}
            validationCounts={uploadDetailValidationSummary?.counts}
            itemValidations={uploadDetailValidation}
          />

          <DebugInfoSection
            sectionLabel={'Scans'}
            isLoading={isLoadingScans || isFetchingScans}
            isError={isScansError}
            jsonObjectOrArrayOfObjects={scans || []}
            jsonLabels={scanLabels}
            showID={false}
            validationSummary={scanValidationSummary?.result}
            validationCounts={scanValidationSummary?.counts}
            itemValidations={scanValidations}
            useAccordionForLargeLists={true}
            accordionThreshold={5}
          />

          <DebugInfoSection
            sectionLabel={'Alleys'}
            isLoading={isLoadingAlleys || isFetchingAlleys}
            isError={isAlleysError}
            jsonObjectOrArrayOfObjects={sortedAlleys || []}
            jsonLabels={alleyLabels}
            showID={false}
            validationSummary={alleyValidationSummary?.result}
            validationCounts={alleyValidationSummary?.counts}
            itemValidations={alleyValidations}
            useAccordionForLargeLists={true}
            accordionThreshold={5}
          />

          <DebugInfoSection
            sectionLabel={'Uploads'}
            isLoading={isLoadingUploads || isFetchingUploads}
            isError={isUploadsError}
            jsonObjectOrArrayOfObjects={sortedUploads || []}
            jsonLabels={uploadLabels}
            showID={false}
            validationSummary={uploadValidationSummary?.result}
            validationCounts={uploadValidationSummary?.counts}
            itemValidations={uploadValidations}
            useAccordionForLargeLists={true}
            accordionThreshold={5}
          />
        </>
      )}
    </Stack>
  );

  return content;
};
